import { AnalysisMode, IndexSymbol, type OptionData, type VarianceResult } from '../types/index'
import { performMarketCalculations } from '@options-oracle/core-logic'
import type { ValidatedOptionData } from './schema'

export interface SupportResistanceResult {
    support: number
    support2: number
    resistance: number
    resistance2: number
    shift: number
    shiftOld?: number
    maxCallOI: number
    maxPutOI: number
    maxCallVol: number
    maxPutVol: number
    pcr: number
    pcrOI: number
    pcrVolume: number
    maxPainStrike: number
    totalCallOI: number
    totalPutOI: number
    // Helper accessors for downstream convenience
    supportLtp?: number
    resistanceLtp?: number
    support2Ltp?: number
    resistance2Ltp?: number
    atmStrike?: number
    ceOtmStrike?: number
    peOtmStrike?: number
    shiftV: number
    shiftV_OI: number
    shiftV_Vol: number
    v1Strike: number
    v2Strike: number
    v3Strike: number
    v1Vol: number
    v2Vol: number
    v3Vol: number
}

/**
 * Calculates Support, Resistance, Shift, and Variance using Standardized Core Logic
 */
export function calculateSupportResistance(
    options: OptionData[],
    spotPrice: number,
    mode: 'OI' | 'VOLUME' = 'OI',
    range: number = 20,
    symbol: IndexSymbol = IndexSymbol.NIFTY,
    baseOptions: OptionData[] = [],
    baseSpot: number = 0,
): SupportResistanceResult {
    if (!options || options.length === 0)
        return {
            support: 0,
            support2: 0,
            resistance: 0,
            resistance2: 0,
            shift: 0,
            shiftOld: 0,
            maxCallOI: 0,
            maxPutOI: 0,
            maxCallVol: 0,
            maxPutVol: 0,
            pcr: 0,
            pcrOI: 0,
            pcrVolume: 0,
            maxPainStrike: 0,
            totalCallOI: 0,
            totalPutOI: 0,
            shiftV: 0,
            shiftV_OI: 0,
            shiftV_Vol: 0,
            v1Strike: 0,
            v2Strike: 0,
            v3Strike: 0,
            v1Vol: 0,
            v2Vol: 0,
            v3Vol: 0,
        }

    const analysisMode = mode === 'OI' ? AnalysisMode.OI : AnalysisMode.VOLUME

    // Cast Data to Validated schema (types are compatible)
    const result = performMarketCalculations(
        options as unknown as ValidatedOptionData[],
        spotPrice,
        {
            mode: analysisMode,
            range: range,
            symbol: symbol,
            atmStrike: undefined, // Auto-detect
        },
        baseOptions as unknown as ValidatedOptionData[],
        baseSpot,
    )

    return {
        support: result.supportStrike,
        support2: result.supportStrike2,
        resistance: result.resistanceStrike,
        resistance2: result.resistanceStrike2,
        shift: result.shift,
        shiftOld: result.shiftOld,
        maxCallOI: result.maxCallOI,
        maxPutOI: result.maxPutOI,
        maxCallVol: result.maxCallVol,
        maxPutVol: result.maxPutVol,
        pcr: result.pcr,
        pcrOI: result.pcrOI,
        pcrVolume: result.pcrVolume,
        maxPainStrike: result.maxPainStrike,
        totalCallOI: result.totalCallOI || 0,
        totalPutOI: result.totalPutOI || 0,
        atmStrike: result.atmStrike,
        ceOtmStrike: result.ceOtmStrike,
        peOtmStrike: result.peOtmStrike,
        shiftV: result.shiftV || 0,
        shiftV_OI: result.shiftV_OI || 0,
        shiftV_Vol: result.shiftV_Vol || 0,
        v1Strike: result.v1Strike || 0,
        v2Strike: result.v2Strike || 0,
        v3Strike: result.v3Strike || 0,
        v1Vol: result.v1Vol || 0,
        v2Vol: result.v2Vol || 0,
        v3Vol: result.v3Vol || 0,
    }
}

/**
 * Wrapper for calculating variance with optional base time comparison
 * Now effectively delegates entirely to performServerSideCalculations
 */
export const calculateDynamicVariance = (
    options: OptionData[],
    spotPrice: number,
    range: number,
    step: number,
    mode: AnalysisMode,
    baseOptions?: OptionData[],
    baseSpot?: number,
    disableFallback: boolean = false,
): VarianceResult => {
    // If fallback disabled (requested base time missing), return empty zeros
    if (disableFallback && (!baseOptions || baseOptions.length === 0)) {
        return {
            finalVariance: 0,
            ceCount: 0,
            peCount: 0,
            totalCount: 0,
            maxDiff: 0,
            minDiff: 0,
            maxStrike: 0,
            minStrike: 0,
        }
    }

    const result = performMarketCalculations(
        options as unknown as ValidatedOptionData[],
        spotPrice,
        {
            mode: mode,
            range: range,
            symbol: IndexSymbol.NIFTY, // Default for variance calculation if not provided
            atmStrike: undefined, // Auto-detect
        },
        (baseOptions || []) as unknown as ValidatedOptionData[],
        baseSpot || 0,
    )

    // If core returns null variance (e.g. empty inputs), provide defaults
    return (
        result.variance || {
            finalVariance: 0,
            ceCount: 0,
            peCount: 0,
            totalCount: 0,
            maxDiff: 0,
            minDiff: 0,
            maxStrike: 0,
            minStrike: 0,
        }
    )
}

// Legacy Helper - kept for compatibility but implemented using new core
export function calculateVarianceMetrics(options: OptionData[], mode: 'OI' | 'VOLUME' = 'OI'): VarianceResult {
    return calculateDynamicVariance(
        options,
        0, // No spot needed for default Metrics if just sorting
        1000, // Large range
        50, // Default step
        mode === 'OI' ? AnalysisMode.OI : AnalysisMode.VOLUME,
        [], // No Base
        0,
    )
}

// Legacy alias which might be called by older code (though we updated most)
// We just alias it to current functionality
export const calculateIntradayVariance = (
    options: OptionData[],
    spotPrice: number,
    range: number,
    step: number,
    mode: AnalysisMode,
    disableFallback: boolean = false,
) => {
    // The previous implementation didn't pass base time options, but to work as expected
    // it requires them. The new architecture passes them directly into `calculateDynamicVariance`
    // from useDashboardData or trpc.
    return calculateDynamicVariance(options, spotPrice, range, step, mode, [], 0, disableFallback)
}
